/**
 * Domínio: opções dos filtros da /auditoria — GUPM-335 (adendo 03/10).
 *
 * As opções saem do catálogo do domínio, não das linhas carregadas. Antes elas
 * eram derivadas da página na tela — e a página eram as 200 linhas mais
 * recentes, quase todas do cron de perda de sinal: "Registro TARP" nem aparecia
 * no filtro, e quando aparecia filtrava só dentro dessas 200.
 *
 * TS puro: sem React, sem Supabase, sem DOM.
 */

import { entidadesConhecidas } from './index';
import { acoesConhecidas, rotuloDaAcao } from './descrever-acao';

export interface OpcaoDeEntidade {
  /** Valor do <select>: as entidades separadas por vírgula. */
  valor: string;
  rotulo: string;
  modulo: string;
  /** Nomes de tabela que a opção cobre (`users`/`engineers`/`app_users` são a mesma). */
  entidades: string[];
}

/**
 * Uma opção por par módulo/rótulo. Entidades com o mesmo rótulo são a mesma
 * coisa em épocas diferentes (a tabela de usuários já se chamou `engineers` e
 * `users`) — a pessoa escolhe "Usuário" e o filtro cobre as três.
 * Ordenadas por módulo e depois por rótulo.
 */
export function opcoesDeEntidade(): OpcaoDeEntidade[] {
  const grupos = new Map<string, OpcaoDeEntidade>();
  for (const [entidade, d] of entidadesConhecidas()) {
    const chave = `${d.module}|${d.label}`;
    const g = grupos.get(chave);
    if (g) g.entidades.push(entidade);
    else grupos.set(chave, { valor: '', rotulo: d.label, modulo: d.module, entidades: [entidade] });
  }
  return [...grupos.values()]
    .map((g) => ({ ...g, valor: g.entidades.join(',') }))
    .sort((a, b) => a.modulo.localeCompare(b.modulo, 'pt-BR') || a.rotulo.localeCompare(b.rotulo, 'pt-BR'));
}

export interface OpcaoDeAcao {
  valor: string;
  rotulo: string;
}

/** Ações conhecidas com o rótulo que a pessoa lê, em ordem alfabética do rótulo. */
export function opcoesDeAcao(): OpcaoDeAcao[] {
  return acoesConhecidas()
    .map((valor) => ({ valor, rotulo: rotuloDaAcao(valor) }))
    .sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR'));
}

/** Filtro "Quem": ações de pessoa identificada × sistema/carga/antes do login. */
export type FiltroQuem = 'pessoas' | 'sistema';

/**
 * Texto do erro de carga. Estouro de tempo (57014) tem causa conhecida — filtro
 * de ator num período longo varre a trilha inteira — e uma saída que a pessoa
 * consegue executar: reduzir o período.
 */
export function mensagemDeFalhaDaAuditoria(erro: { code?: string; message: string }): string {
  if (erro.code === '57014') {
    return 'A consulta demorou demais e foi cancelada pelo banco. Reduza o período (ou tire o filtro de Ator) e tente de novo.';
  }
  return `Falha ao carregar auditoria: ${erro.message}`;
}
